(function () {
  'use strict';

  const DEFAULT_API_ENDPOINT = 'https://api-dev.tennesseeknifecompany.com/graphql';
  const STORY_NODE_FIELDS = 'id display_name city state harvest_name harvested_species harvest_type title description model_name status is_featured is_hall_of_honour published_at category { id slug label sort_order } author { id name email } media { id url media_type sort_order created_at } created_at updated_at';
  const STORY_CONNECTION_FIELDS = `edges { cursor node { ${STORY_NODE_FIELDS} } } pageInfo { hasNextPage endCursor }`;
  const DEFAULT_STORIES_QUERY = `query PublicStories($filter: StoriesFilterInput) { stories(filter: $filter) { ${STORY_CONNECTION_FIELDS} } }`;
  const MODAL_ID = 'tkc-public-story-detail-modal';
  const MODAL_STYLE_ID = 'tkc-public-story-detail-modal-styles';

  let lastFocusedElement = null;

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getYouTubeEmbedUrl(value, autoplay) {
    const rawUrl = String(value || '').trim();
    if (!rawUrl) return '';

    let url;
    try {
      url = new URL(rawUrl);
    } catch (error) {
      return '';
    }

    const host = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
    const segments = url.pathname.split('/').filter(Boolean);
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = segments[0] || '';
    } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      if (segments[0] === 'watch') {
        videoId = url.searchParams.get('v') || '';
      } else if (['embed', 'shorts', 'live'].indexOf(segments[0]) !== -1) {
        videoId = segments[1] || '';
      }
    }

    videoId = String(videoId || '').replace(/[^A-Za-z0-9_-]/g, '');
    if (!videoId) return '';

    return `https://www.youtube.com/embed/${videoId}?rel=0${autoplay ? '&autoplay=1&mute=1&playsinline=1' : ''}`;
  }

  function addResourceHint(rel, href, crossOrigin) {
    if (typeof document === 'undefined' || !document.head) return;
    if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;

    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (crossOrigin) link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function warmYouTubeConnections() {
    [
      'https://www.youtube.com',
      'https://www.youtube-nocookie.com',
      'https://i.ytimg.com',
      'https://s.ytimg.com',
      'https://www.google.com'
    ].forEach((href) => {
      addResourceHint('dns-prefetch', href, false);
      addResourceHint('preconnect', href, true);
    });
  }

  function preloadYouTubeEmbed(value) {
    const embedUrl = getYouTubeEmbedUrl(value, false);
    if (!embedUrl || typeof document === 'undefined' || !document.body) return;

    warmYouTubeConnections();

    let frame = document.getElementById('tkc-youtube-embed-preload');
    if (!frame) {
      frame = document.createElement('iframe');
      frame.id = 'tkc-youtube-embed-preload';
      frame.title = 'YouTube player preload';
      frame.loading = 'eager';
      frame.tabIndex = -1;
      frame.setAttribute('aria-hidden', 'true');
      frame.style.cssText = 'position:absolute;width:1px;height:1px;left:-9999px;top:-9999px;opacity:0;pointer-events:none;border:0;';
      document.body.appendChild(frame);
    }

    if (frame.dataset.src !== embedUrl) {
      frame.dataset.src = embedUrl;
      frame.src = embedUrl;
    }
  }

  function renderVideoPlayer(url, className, autoplay) {
    const embedUrl = getYouTubeEmbedUrl(url, autoplay);

    if (embedUrl) {
      warmYouTubeConnections();
      return `<iframe class="${className}" src="${escapeHtml(embedUrl)}" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="${autoplay ? 'eager' : 'lazy'}"></iframe>`;
    }

    return `<video class="${className}" controls ${autoplay ? 'autoplay muted playsinline' : 'preload="metadata" playsinline'} src="${escapeHtml(url)}"></video>`;
  }

  function renderVideoPreview(url, className) {
    const isYouTube = !!getYouTubeEmbedUrl(url, false);
    if (isYouTube) preloadYouTubeEmbed(url);

    const poster = isYouTube
      ? '<span class="tkc-story-modal__video-poster">Video</span>'
      : `<video class="tkc-story-modal__video-poster" muted preload="metadata" playsinline src="${escapeHtml(url)}"></video>`;

    return `
      <span class="${className} tkc-story-modal__video-preview">
        ${poster}
        <span class="tkc-story-modal__play-icon" aria-hidden="true"></span>
      </span>
    `;
  }

  function toBoolean(value) {
    return value === true || value === 1 || value === '1' || normalize(value) === 'true';
  }

  function compactText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function getPlainParagraphs(value) {
    return String(value || '')
      .replace(/\r/g, '')
      .replace(/<\/p>|<br\s*\/?>|<\/div>|<\/li>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
      .split(/\n+/)
      .map(compactText)
      .filter(Boolean);
  }

  function formatDate(value) {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  }

  function cleanFilter(filter) {
    return Object.keys(filter || {}).reduce((result, key) => {
      if (filter[key] !== null && filter[key] !== undefined && filter[key] !== '') {
        result[key] = filter[key];
      }

      return result;
    }, {});
  }

  function getStoryConnection(data) {
    if (!data || typeof data !== 'object') return null;

    return data.stories ||
      data.allStories ||
      data.publicStories ||
      Object.keys(data)
        .map((key) => data[key])
        .find((value) => value && typeof value === 'object' && (Array.isArray(value.edges) || Array.isArray(value.nodes))) ||
      null;
  }

  function extractGraphqlErrorMessage(error) {
    if (!error) return '';

    const extensions = error.extensions || {};
    const nested = extensions.originalError || extensions.exception || extensions.response || {};
    const detail = nested.message || extensions.message;

    if (Array.isArray(detail)) {
      return detail.filter(Boolean).join(' ');
    }

    return detail || error.message || '';
  }

  async function request(endpoint, query, variables) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'apollo-require-preflight': 'true',
        'ngrok-skip-browser-warning': 'true'
      },
      body: JSON.stringify({ query, variables: variables || {} })
    });

    const payload = await response.json().catch(() => null);

    if (payload && Array.isArray(payload.errors) && payload.errors.length) {
      const message = payload.errors.map(extractGraphqlErrorMessage).filter(Boolean).join(' ');
      throw new Error(message || 'Stories could not be loaded.');
    }

    if (!response.ok) {
      throw new Error((payload && payload.message) || 'Stories could not be loaded.');
    }

    return payload ? payload.data : null;
  }

  function sortMedia(media) {
    return (media || []).filter(Boolean).slice().sort((first, second) => {
      const firstOrder = typeof first.sort_order === 'number' ? first.sort_order : 0;
      const secondOrder = typeof second.sort_order === 'number' ? second.sort_order : 0;
      return firstOrder - secondOrder;
    });
  }

  function mapMedia(media) {
    return sortMedia(media).map((item) => ({
      id: item.id || item.url || '',
      url: item.url || '',
      mediaType: item.media_type || item.mediaType || '',
      createdAt: item.created_at || item.createdAt || ''
    })).filter((item) => item.url);
  }

  function getImageUrl(story) {
    const media = story && story.length === undefined ? mapMedia(story.media) : (story || []);
    const image = media.find((item) => normalize(item.mediaType || item.media_type) !== 'video' && item.url) || media.find((item) => item.url);
    return image ? image.url : '';
  }

  function getHunterName(story) {
    return story.display_name ||
      (story.author && story.author.name) ||
      story.harvest_name ||
      story.title ||
      'TKC Story';
  }

  function getAnimalType(story) {
    return story.harvested_species ||
      story.harvest_type ||
      story.model_name ||
      (story.category && story.category.label) ||
      'Harvest Story';
  }

  function mapStory(node) {
    const hunterName = getHunterName(node);
    const animalType = getAnimalType(node);
    const media = mapMedia(node.media);
    const stateName = node.state || node.city || '';
    const category = node.category || {};
    const author = node.author || {};
    const paragraphs = getPlainParagraphs(node.description);

    return {
      id: node.id || `${hunterName}-${animalType}-${stateName}`,
      hunterName,
      animalType,
      state: node.state || stateName,
      city: node.city || '',
      harvestName: node.harvest_name || '',
      harvestedSpecies: node.harvested_species || '',
      harvestType: node.harvest_type || '',
      title: node.title || '',
      description: node.description || '',
      descriptionText: paragraphs.join(' '),
      modelName: node.model_name || '',
      status: node.status || '',
      isFeatured: toBoolean(node.is_featured),
      isHallOfHonor: toBoolean(node.is_hall_of_honour) || toBoolean(node.is_hall_of_honor),
      categoryLabel: category.label || category.slug || '',
      authorName: author.name || '',
      authorEmail: author.email || '',
      media,
      imageUrl: getImageUrl(media),
      storyUrl: `#story-${encodeURIComponent(node.id || normalize(hunterName).replace(/\s+/g, '-'))}`,
      publishedAt: node.published_at || node.created_at || ''
    };
  }

  function isApprovedStory(story) {
    const status = normalize(story.status);
    return !status || ['approved', 'published', 'live'].includes(status);
  }

  function uniqueValues(items, key) {
    return [...new Set(items.map((item) => item[key]).filter(Boolean))].sort((first, second) => first.localeCompare(second));
  }

  function setSelectOptions(select, values) {
    if (!select) return;

    const defaultLabel = select.querySelector('option') ? select.querySelector('option').textContent : 'All';
    select.innerHTML = '';

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = defaultLabel;
    select.appendChild(defaultOption);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });
  }

  function buildPagination(page, limit, total) {
    const totalPages = Math.max(1, Math.ceil(total / limit));

    return {
      page: Math.min(page, totalPages),
      limit,
      total,
      totalPages,
      hasNext: page < totalPages,
      hasPrev: page > 1
    };
  }

  function getStoryKey(story) {
    return String(story.id || story.storyUrl || story.hunterName || story.title || 'tkc-story');
  }

  function getLocation(story) {
    return [story.city, story.state].filter(Boolean).join(', ');
  }

  function getStoryBadge(story, config) {
    if (config.hallOfHonorOnly || story.isHallOfHonor) return config.hallOfHonorBadgeLabel || 'Hall of Honor';
    if (story.isFeatured) return config.featuredBadgeLabel || 'Featured Story';
    return config.defaultBadgeLabel || 'Harvest Story';
  }

  function storyCardTemplate(story, config) {
    const cardLabel = escapeHtml(config.cardButtonLabel || 'View Story');
    const imageAlt = escapeHtml(story.title || story.hunterName);
    const imageMarkup = story.imageUrl
      ? `<img class="hof-card__image" src="${escapeHtml(story.imageUrl)}" alt="${imageAlt}" loading="lazy">`
      : `<span class="hof-card__image" aria-label="${escapeHtml(config.placeholderAlt || 'TKC story')}"></span>`;
    const ctaMarkup = config.showCardCta === false ? '' : `<span class="hof-card__cta">${cardLabel} <span aria-hidden="true">&rarr;</span></span>`;
    const metaParts = [story.animalType, story.state].filter(Boolean);
    const storyId = escapeHtml(getStoryKey(story));
    const detailLabel = `Read ${escapeHtml(story.hunterName)}'s First Harvest story`;

    return `
      <article class="hof-card">
        <button class="hof-card__media-button" type="button" data-story-detail-button data-story-id="${storyId}" aria-label="${detailLabel}">
          <span class="hof-card__media">
            ${imageMarkup}
          </span>
        </button>
        <div class="hof-card__content">
          <h2 class="hof-card__name">
            <button class="hof-card__name-button" type="button" data-story-detail-button data-story-id="${storyId}" aria-label="${detailLabel}">${escapeHtml(story.hunterName)}</button>
          </h2>
          <p class="hof-card__meta">${metaParts.map((part) => `<span>${escapeHtml(part)}</span>`).join('<span aria-hidden="true">&bull;</span>')}</p>
          ${ctaMarkup}
        </div>
      </article>
    `;
  }

  function getDetailRows(story) {
    const rows = [
      ['Hunter', story.hunterName],
      ['Story title', story.title],
      ['Harvest name', story.harvestName],
      ['Species', story.harvestedSpecies || story.animalType],
      ['Harvest type', story.harvestType],
      ['Knife model', story.modelName],
      ['Location', getLocation(story)],
      ['Category', story.categoryLabel],
      ['Published', formatDate(story.publishedAt)]
    ];

    return rows.filter((row) => compactText(row[1]));
  }

  function detailRowsMarkup(story) {
    const seen = {};

    return getDetailRows(story).filter((row) => {
      const key = `${row[0]}:${row[1]}`;
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    }).map((row) => `
      <div class="tkc-story-modal__fact">
        <dt>${escapeHtml(row[0])}</dt>
        <dd>${escapeHtml(row[1])}</dd>
      </div>
    `).join('');
  }

  function descriptionMarkup(story) {
    const paragraphs = getPlainParagraphs(story.description);
    if (!paragraphs.length) {
      return '<p>No story description has been added yet.</p>';
    }

    return paragraphs.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join('');
  }

  function modalSlideMarkup(item, story, index) {
    const hidden = index === 0 ? '' : ' hidden';

    if (normalize(item.mediaType) === 'video') {
      return `
        <div class="tkc-story-modal__slide${index === 0 ? ' is-active' : ''}" data-story-modal-slide${hidden}>
          <button class="tkc-story-modal__media-open tkc-story-modal__video-open" type="button" data-story-media-open data-media-type="video" data-media-url="${escapeHtml(item.url)}" aria-label="Play story video">
            ${renderVideoPreview(item.url, 'tkc-story-modal__video')}
          </button>
        </div>
      `;
    }

    return `
      <div class="tkc-story-modal__slide${index === 0 ? ' is-active' : ''}" data-story-modal-slide${hidden}>
        <button class="tkc-story-modal__media-open" type="button" data-story-media-open data-media-type="image" data-media-url="${escapeHtml(item.url)}" aria-label="Enlarge story image">
          <img class="tkc-story-modal__image" src="${escapeHtml(item.url)}" alt="${escapeHtml(story.title || story.hunterName)} media" loading="${index === 0 ? 'eager' : 'lazy'}">
        </button>
      </div>
    `;
  }

  function modalHeroMarkup(story, config) {
    const media = story.media && story.media.length
      ? story.media
      : (story.imageUrl ? [{ url: story.imageUrl, mediaType: 'image' }] : []);

    if (!media.length) {
      return `<div class="tkc-story-modal__placeholder">${escapeHtml(config.placeholderText || 'TKC Story')}</div>`;
    }

    const visibleMedia = media.slice(0, 12);
    const sliderControls = visibleMedia.length > 1
      ? `
        <button class="tkc-story-modal__slider-btn is-prev" type="button" data-story-slider-action="prev" aria-label="Previous media">&lsaquo;</button>
        <button class="tkc-story-modal__slider-btn is-next" type="button" data-story-slider-action="next" aria-label="Next media">&rsaquo;</button>
        <span class="tkc-story-modal__slider-count" data-story-slider-count>1 / ${escapeHtml(visibleMedia.length)}</span>
      `
      : '';

    return `
      <div class="tkc-story-modal__slider" data-story-media-slider data-index="0">
        ${visibleMedia.map((item, index) => modalSlideMarkup(item, story, index)).join('')}
        ${sliderControls}
      </div>
    `;
  }

  function modalGalleryMarkup() {
    return '';
  }

  function ensureModalStyles() {
    if (document.getElementById(MODAL_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = MODAL_STYLE_ID;
    style.textContent = `
      html.tkc-story-modal-open { overflow: hidden; }
      .tkc-story-modal[hidden] { display: none; }
      .tkc-story-modal {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: grid;
        place-items: center;
        padding: 24px;
      }
      .tkc-story-modal__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(6, 5, 4, 0.78);
        backdrop-filter: blur(8px);
      }
      .tkc-story-modal__panel {
        position: relative;
        z-index: 1;
        width: min(1120px, 100%);
        max-height: min(780px, calc(100vh - 48px));
        overflow: hidden;
        border: 1px solid rgba(217, 197, 163, 0.28);
        border-radius: 8px;
        background: #15110d;
        color: #fff;
        box-shadow: 0 28px 80px rgba(0, 0, 0, 0.62);
      }
      .tkc-story-modal__close {
        position: absolute;
        top: 14px;
        right: 14px;
        z-index: 3;
        width: 42px;
        height: 42px;
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 999px;
        background: rgba(0, 0, 0, 0.58);
        color: #fff;
        font-size: 28px;
        line-height: 1;
        cursor: pointer;
      }
      .tkc-story-modal__close:hover,
      .tkc-story-modal__close:focus-visible {
        background: #8b6b42;
        outline: none;
      }
      .tkc-story-modal__body {
        display: grid;
        grid-template-columns: minmax(0, 1.05fr) minmax(340px, 0.95fr);
        max-height: min(780px, calc(100vh - 48px));
        overflow: auto;
      }
      .tkc-story-modal__media {
        position: relative;
        min-height: 560px;
        background: #0d0b08;
      }
      .tkc-story-modal__slider,
      .tkc-story-modal__slide,
      .tkc-story-modal__media-open {
        width: 100%;
        height: 100%;
        min-height: 560px;
      }
      .tkc-story-modal__slider {
        position: relative;
      }
      .tkc-story-modal__slide[hidden] {
        display: none;
      }
      .tkc-story-modal__media-open {
        display: block;
        padding: 0;
        border: 0;
        background: transparent;
        cursor: zoom-in;
      }
      .tkc-story-modal__video-open {
        width: 100%;
        height: 100%;
        cursor: pointer;
      }
      .tkc-story-modal__media-open:focus-visible {
        outline: 3px solid rgba(217, 197, 163, 0.55);
        outline-offset: -6px;
      }
      .tkc-story-modal__image,
      .tkc-story-modal__video,
      .tkc-story-modal__placeholder {
        width: 100%;
        height: 100%;
        min-height: 560px;
        object-fit: contain;
        display: block;
      }
      .tkc-story-modal__video {
        background: #080705;
      }
      iframe.tkc-story-modal__video {
        border: 0;
      }
      .tkc-story-modal__video-preview {
        position: relative;
        display: grid;
        place-items: center;
        overflow: hidden;
      }
      .tkc-story-modal__video-preview video,
      .tkc-story-modal__video-poster {
        display: block;
        width: 100%;
        height: 100%;
        min-height: 0;
        object-fit: contain;
      }
      .tkc-story-modal__video-poster {
        display: grid;
        place-items: center;
        color: rgba(255, 255, 255, 0.76);
        font-size: 13px;
        font-weight: 900;
        letter-spacing: 0;
        text-transform: uppercase;
      }
      .tkc-story-modal__play-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        display: grid;
        width: 74px;
        height: 74px;
        place-items: center;
        border: 2px solid rgba(255, 255, 255, 0.86);
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.58);
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.42);
        pointer-events: none;
        transform: translate(-50%, -50%);
      }
      .tkc-story-modal__play-icon::before {
        content: "";
        display: block;
        margin-left: 6px;
        border-style: solid;
        border-width: 15px 0 15px 24px;
        border-color: transparent transparent transparent #ffffff;
      }
      .tkc-story-modal__placeholder {
        display: grid;
        place-items: center;
        padding: 28px;
        background: linear-gradient(135deg, #2a2118, #0f0d0a);
        color: rgba(255, 255, 255, 0.74);
        font-weight: 900;
        letter-spacing: 0.08em;
        text-align: center;
        text-transform: uppercase;
      }
      .tkc-story-modal__media::after {
        content: "";
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: linear-gradient(90deg, transparent 58%, rgba(21, 17, 13, 0.74) 100%);
      }
      .tkc-story-modal__slider-btn {
        position: absolute;
        top: 50%;
        z-index: 2;
        display: inline-grid;
        width: 42px;
        height: 42px;
        place-items: center;
        border: 1px solid rgba(255, 255, 255, 0.24);
        border-radius: 999px;
        background: rgba(0, 0, 0, 0.58);
        color: #fff;
        font-size: 30px;
        line-height: 1;
        transform: translateY(-50%);
        cursor: pointer;
      }
      .tkc-story-modal__slider-btn:hover,
      .tkc-story-modal__slider-btn:focus-visible {
        background: #8b6b42;
        outline: none;
      }
      .tkc-story-modal__slider-btn.is-prev {
        left: 16px;
      }
      .tkc-story-modal__slider-btn.is-next {
        right: 16px;
      }
      .tkc-story-modal__slider-count {
        position: absolute;
        left: 50%;
        bottom: 18px;
        z-index: 2;
        padding: 7px 12px;
        border-radius: 999px;
        background: rgba(0, 0, 0, 0.62);
        color: #fff;
        font-size: 12px;
        font-weight: 900;
        transform: translateX(-50%);
      }
      .tkc-story-modal__content {
        position: relative;
        padding: 48px 42px 38px;
        background:
          linear-gradient(180deg, rgba(139, 107, 66, 0.16), transparent 260px),
          #15110d;
      }
      .tkc-story-modal__kicker {
        margin: 0 0 12px;
        color: #d9c5a3;
        font-size: 12px;
        font-weight: 900;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .tkc-story-modal__title {
        margin: 0;
        color: #fff;
        font-size: clamp(30px, 4vw, 52px);
        line-height: 0.96;
        text-transform: uppercase;
      }
      .tkc-story-modal__subtitle {
        margin: 14px 0 0;
        color: rgba(255, 255, 255, 0.76);
        font-size: 16px;
        font-weight: 800;
        line-height: 1.45;
      }
      .tkc-story-modal__description {
        margin-top: 26px;
        color: rgba(255, 255, 255, 0.84);
        font-size: 16px;
        line-height: 1.68;
      }
      .tkc-story-modal__description p {
        margin: 0 0 14px;
      }
      .tkc-story-modal__facts {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
        margin: 26px 0 0;
      }
      .tkc-story-modal__fact {
        padding: 13px 14px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.05);
      }
      .tkc-story-modal__fact dt {
        margin: 0 0 5px;
        color: #d9c5a3;
        font-size: 11px;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .tkc-story-modal__fact dd {
        margin: 0;
        color: #fff;
        font-size: 14px;
        font-weight: 800;
        line-height: 1.35;
      }
      .tkc-story-modal__gallery {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
        margin-top: 24px;
      }
      .tkc-story-modal__gallery-image,
      .tkc-story-modal__gallery-video {
        width: 100%;
        aspect-ratio: 1;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid rgba(255, 255, 255, 0.12);
        background: #080705;
      }
      .tkc-story-lightbox[hidden] {
        display: none;
      }
      .tkc-story-lightbox {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: grid;
        place-items: center;
        padding: 20px;
      }
      .tkc-story-lightbox__backdrop {
        position: absolute;
        inset: 0;
        border: 0;
        background: rgba(0, 0, 0, 0.82);
        cursor: zoom-out;
      }
      .tkc-story-lightbox__panel {
        position: relative;
        z-index: 1;
        width: min(1180px, 100%);
        max-height: calc(100vh - 40px);
        padding: 18px;
        border-radius: 8px;
        background: #080705;
      }
      .tkc-story-lightbox__close {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 2;
        width: 42px;
        height: 42px;
        border: 1px solid rgba(255, 255, 255, 0.22);
        border-radius: 999px;
        background: rgba(0, 0, 0, 0.68);
        color: #fff;
        font-size: 28px;
        line-height: 1;
        cursor: pointer;
      }
      .tkc-story-lightbox__body img,
      .tkc-story-lightbox__body video,
      .tkc-story-lightbox__body iframe {
        display: block;
        width: 100%;
        max-height: calc(100vh - 76px);
        border-radius: 6px;
        background: #080705;
        object-fit: contain;
      }
      .tkc-story-lightbox__body iframe {
        aspect-ratio: 16 / 9;
        border: 0;
      }
      @media screen and (max-width: 860px) {
        .tkc-story-modal { padding: 14px; }
        .tkc-story-modal__panel,
        .tkc-story-modal__body {
          max-height: calc(100vh - 28px);
        }
        .tkc-story-modal__body {
          grid-template-columns: 1fr;
        }
        .tkc-story-modal__media,
        .tkc-story-modal__image,
        .tkc-story-modal__video,
        .tkc-story-modal__slider,
        .tkc-story-modal__slide,
        .tkc-story-modal__media-open,
        .tkc-story-modal__placeholder {
          min-height: 320px;
          max-height: 420px;
        }
        .tkc-story-modal__media::after {
          background: linear-gradient(180deg, transparent 58%, rgba(21, 17, 13, 0.82) 100%);
        }
        .tkc-story-modal__content {
          padding: 30px 22px 26px;
        }
        .tkc-story-modal__facts {
          grid-template-columns: 1fr;
        }
        .tkc-story-modal__gallery {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
    `;
    document.head.appendChild(style);
  }

  function closeStoryModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    closeStoryLightbox();
    modal.hidden = true;
    document.documentElement.classList.remove('tkc-story-modal-open');

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function isStoryLightboxOpen() {
    const lightbox = document.getElementById('tkc-public-story-media-lightbox');
    return !!(lightbox && !lightbox.hidden);
  }

  function closeStoryLightbox() {
    const lightbox = document.getElementById('tkc-public-story-media-lightbox');
    if (!lightbox) return;

    const body = lightbox.querySelector('[data-story-lightbox-body]');
    lightbox.hidden = true;
    if (body) body.innerHTML = '';
  }

  function ensureStoryLightbox() {
    let lightbox = document.getElementById('tkc-public-story-media-lightbox');
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.id = 'tkc-public-story-media-lightbox';
      lightbox.className = 'tkc-story-lightbox';
      lightbox.hidden = true;
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Story media preview');
      lightbox.innerHTML = `
        <button class="tkc-story-lightbox__backdrop" type="button" data-story-lightbox-close aria-label="Close media preview"></button>
        <div class="tkc-story-lightbox__panel">
          <button class="tkc-story-lightbox__close" type="button" data-story-lightbox-close aria-label="Close media preview">&times;</button>
          <div class="tkc-story-lightbox__body" data-story-lightbox-body></div>
        </div>
      `;
      document.body.appendChild(lightbox);
    }

    if (lightbox.dataset.storyLightboxBound !== 'true') {
      lightbox.dataset.storyLightboxBound = 'true';
      lightbox.addEventListener('click', (event) => {
        if (event.target.closest('[data-story-lightbox-close]')) {
          closeStoryLightbox();
        }
      });
    }

    return lightbox;
  }

  function openStoryMediaLightbox(button) {
    if (!button) return;

    const url = button.dataset.mediaUrl || '';
    const type = button.dataset.mediaType || 'image';
    if (!url) return;

    const lightbox = ensureStoryLightbox();
    const body = lightbox.querySelector('[data-story-lightbox-body]');
    if (!body) return;

    body.innerHTML = type === 'video'
      ? renderVideoPlayer(url, 'tkc-story-lightbox__video', true)
      : `<img src="${escapeHtml(url)}" alt="">`;
    lightbox.hidden = false;

    const close = lightbox.querySelector('[data-story-lightbox-close]');
    if (close) close.focus();
  }

  function setStorySliderIndex(slider, index) {
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('[data-story-modal-slide]'));
    if (!slides.length) return;

    const nextIndex = (index + slides.length) % slides.length;
    slider.dataset.index = String(nextIndex);
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === nextIndex;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
    });

    const count = slider.querySelector('[data-story-slider-count]');
    if (count) {
      count.textContent = `${nextIndex + 1} / ${slides.length}`;
    }
  }

  function handleStorySliderAction(button) {
    const slider = button ? button.closest('[data-story-media-slider]') : null;
    if (!slider) return;

    const current = Number(slider.dataset.index || 0);
    const delta = button.dataset.storySliderAction === 'prev' ? -1 : 1;
    setStorySliderIndex(slider, current + delta);
  }

  function ensureStoryModal() {
    ensureModalStyles();

    let modal = document.getElementById(MODAL_ID);
    if (!modal) {
      modal = document.createElement('div');
      modal.id = MODAL_ID;
      modal.className = 'tkc-story-modal';
      modal.hidden = true;
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'tkc-public-story-modal-title');
      modal.innerHTML = `
        <div class="tkc-story-modal__backdrop" data-story-modal-close></div>
        <div class="tkc-story-modal__panel">
          <button class="tkc-story-modal__close" type="button" aria-label="Close story details" data-story-modal-close>&times;</button>
          <div class="tkc-story-modal__body" data-story-modal-body></div>
        </div>
      `;
      document.body.appendChild(modal);
    }

    if (modal.dataset.storyModalBound !== 'true') {
      modal.dataset.storyModalBound = 'true';
      modal.addEventListener('click', (event) => {
        const sliderButton = event.target.closest('[data-story-slider-action]');
        if (sliderButton) {
          event.preventDefault();
          handleStorySliderAction(sliderButton);
          return;
        }

        const mediaButton = event.target.closest('[data-story-media-open]');
        if (mediaButton) {
          event.preventDefault();
          openStoryMediaLightbox(mediaButton);
          return;
        }

        if (event.target.closest('[data-story-modal-close]')) {
          closeStoryModal();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        if (isStoryLightboxOpen()) {
          closeStoryLightbox();
          return;
        }

        if (!modal.hidden) {
          closeStoryModal();
        }
      });
    }

    return modal;
  }

  function storyDetailTemplate(story, config) {
    const title = story.title || story.harvestName || `${story.hunterName}'s Story`;
    const subtitleParts = [story.animalType, getLocation(story), formatDate(story.publishedAt)].filter(Boolean);

    return `
      <div class="tkc-story-modal__media">
        ${modalHeroMarkup(story, config)}
      </div>
      <div class="tkc-story-modal__content">
        <p class="tkc-story-modal__kicker">${escapeHtml(getStoryBadge(story, config))}</p>
        <h2 class="tkc-story-modal__title" id="tkc-public-story-modal-title">${escapeHtml(title)}</h2>
        <p class="tkc-story-modal__subtitle">${escapeHtml(story.hunterName)}${subtitleParts.length ? ` | ${escapeHtml(subtitleParts.join(' | '))}` : ''}</p>
        <div class="tkc-story-modal__description">
          ${descriptionMarkup(story)}
        </div>
        <dl class="tkc-story-modal__facts">
          ${detailRowsMarkup(story)}
        </dl>
        ${modalGalleryMarkup(story)}
      </div>
    `;
  }

  function openStoryModal(story, config) {
    const modal = ensureStoryModal();
    const body = modal.querySelector('[data-story-modal-body]');
    if (!body) return;

    lastFocusedElement = document.activeElement;
    body.innerHTML = storyDetailTemplate(story, config);
    modal.hidden = false;
    document.documentElement.classList.add('tkc-story-modal-open');

    const closeButton = modal.querySelector('[data-story-modal-close]');
    if (closeButton && typeof closeButton.focus === 'function') {
      window.setTimeout(() => closeButton.focus(), 20);
    }
  }

  function bindStoryDetails(root, stories, config) {
    root.tkcPublicStories = new Map();
    stories.forEach((story) => {
      root.tkcPublicStories.set(getStoryKey(story), story);
    });

    if (root.dataset.publicStoryDetailsBound === 'true') return;

    root.dataset.publicStoryDetailsBound = 'true';
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-story-detail-button]');
      if (!button || !root.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const storyId = button.getAttribute('data-story-id');
      const story = root.tkcPublicStories && root.tkcPublicStories.get(storyId);
      if (story) {
        openStoryModal(story, config);
      }
    });
  }

  function getElements(root) {
    return {
      grid: root.querySelector('#storiesGrid'),
      loader: root.querySelector('#loader'),
      empty: root.querySelector('#emptyState'),
      resultsCount: root.querySelector('#resultsCount'),
      perPageLabel: root.querySelector('.hof-results-bar span:last-child'),
      pagination: root.querySelector('#pagination'),
      pageNumbers: root.querySelector('#pageNumbers'),
      prevButton: root.querySelector('#prevButton'),
      nextButton: root.querySelector('#nextButton'),
      searchInput: root.querySelector('#searchInput'),
      animalFilter: root.querySelector('#animalFilter'),
      stateFilter: root.querySelector('#stateFilter'),
      resetButton: root.querySelector('#resetButton')
    };
  }

  function debounce(callback, wait) {
    let timeout;

    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  }

  window.TKC = window.TKC || {};

  window.TKC.initPublicStoriesGrid = function initPublicStoriesGrid(root, options) {
    if (!root || root.dataset.publicStoriesReady === 'true') return;

    const config = Object.assign({
      approvedOnly: true,
      cardButtonLabel: 'View Story',
      defaultBadgeLabel: 'Harvest Story',
      endpoint: (window.TKC.authConfig && window.TKC.authConfig.endpoint) || DEFAULT_API_ENDPOINT,
      featuredBadgeLabel: 'Featured Story',
      hallOfHonorBadgeLabel: 'Hall of Honor',
      hallOfHonorOnly: false,
      noResultsCount: 'No stories found',
      pageSize: 20,
      placeholderText: 'TKC Story',
      query: DEFAULT_STORIES_QUERY,
      showCardCta: true
    }, options || {});
    const elements = getElements(root);
    const state = {
      page: 1,
      limit: Math.max(1, Number(config.pageSize) || 20),
      search: '',
      animal: '',
      stateFilter: '',
      stories: []
    };

    root.dataset.publicStoriesReady = 'true';

    if (elements.perPageLabel) {
      elements.perPageLabel.textContent = `${state.limit} per page`;
    }

    function getVisibleStories() {
      return state.stories.filter((story) => {
        const searchBlob = normalize(`${story.hunterName} ${story.animalType} ${story.state} ${story.city} ${story.title} ${story.description}`);
        const matchesSearch = !state.search || searchBlob.includes(normalize(state.search));
        const matchesAnimal = !state.animal || story.animalType === state.animal;
        const matchesState = !state.stateFilter || story.state === state.stateFilter;

        return matchesSearch && matchesAnimal && matchesState;
      });
    }

    function renderGrid(items) {
      elements.grid.innerHTML = items.map((story) => storyCardTemplate(story, config)).join('');
      elements.empty.hidden = items.length !== 0;
    }

    function renderResultsCount(pagination) {
      if (pagination.total === 0) {
        elements.resultsCount.textContent = config.noResultsCount || 'No stories found';
        return;
      }

      const start = (pagination.page - 1) * pagination.limit + 1;
      const end = Math.min(pagination.page * pagination.limit, pagination.total);
      elements.resultsCount.textContent = `Showing ${start}-${end} of ${pagination.total} stories`;
    }

    function renderPagination(pagination) {
      elements.pagination.hidden = pagination.totalPages <= 1;
      elements.prevButton.disabled = !pagination.hasPrev;
      elements.nextButton.disabled = !pagination.hasNext;
      elements.pageNumbers.innerHTML = '';

      for (let page = 1; page <= pagination.totalPages; page += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hof-button hof-page-btn';
        button.textContent = page;
        button.setAttribute('aria-label', `Go to page ${page}`);

        if (page === pagination.page) {
          button.classList.add('is-active');
          button.setAttribute('aria-current', 'page');
        }

        button.addEventListener('click', () => {
          state.page = page;
          render({ scrollToTop: true });
        });

        elements.pageNumbers.appendChild(button);
      }
    }

    function scrollToStoriesGrid() {
      const target = elements.grid || root;
      window.requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    function render(options) {
      const visibleStories = getVisibleStories();
      const pagination = buildPagination(state.page, state.limit, visibleStories.length);
      const start = (pagination.page - 1) * pagination.limit;
      const items = visibleStories.slice(start, start + pagination.limit);

      state.page = pagination.page;
      renderGrid(items);
      renderPagination(pagination);
      renderResultsCount(pagination);

      if (options && options.scrollToTop) {
        scrollToStoriesGrid();
      }
    }

    function populateFilters() {
      setSelectOptions(elements.animalFilter, uniqueValues(state.stories, 'animalType'));
      setSelectOptions(elements.stateFilter, uniqueValues(state.stories, 'state'));
    }

    async function fetchAllStories() {
      const stories = [];
      let after = null;
      let hasNextPage = true;
      let guard = 0;

      while (hasNextPage && guard < 25) {
        guard += 1;

        const data = await request(config.endpoint, config.query, {
          filter: cleanFilter({ first: 100, after })
        });
        const connection = getStoryConnection(data) || { edges: [], nodes: [], pageInfo: {} };
        const nodes = Array.isArray(connection.edges)
          ? connection.edges.map((edge) => edge && edge.node).filter(Boolean)
          : (connection.nodes || []);

        stories.push(...nodes.map(mapStory));
        hasNextPage = Boolean(connection.pageInfo && connection.pageInfo.hasNextPage);
        after = connection.pageInfo && connection.pageInfo.endCursor ? connection.pageInfo.endCursor : null;

        if (!after) {
          hasNextPage = false;
        }
      }

      return stories.filter((story) => {
        const approved = !config.approvedOnly || isApprovedStory(story);
        const hall = !config.hallOfHonorOnly || story.isHallOfHonor;
        return approved && hall;
      });
    }

    async function loadStories() {
      elements.loader.hidden = false;
      elements.grid.innerHTML = '';
      elements.empty.hidden = true;
      elements.pagination.hidden = true;

      try {
        state.stories = await fetchAllStories();
        bindStoryDetails(root, state.stories, config);
        populateFilters();
        elements.loader.hidden = true;
        render();
      } catch (error) {
        elements.loader.hidden = true;
        elements.grid.innerHTML = '';
        elements.empty.hidden = false;
        elements.pagination.hidden = true;
        elements.resultsCount.textContent = error.message || 'Stories could not be loaded.';
      }
    }

    elements.searchInput.addEventListener('input', debounce((event) => {
      state.search = event.target.value;
      state.page = 1;
      render();
    }, 300));

    elements.animalFilter.addEventListener('change', (event) => {
      state.animal = event.target.value;
      state.page = 1;
      render();
    });

    elements.stateFilter.addEventListener('change', (event) => {
      state.stateFilter = event.target.value;
      state.page = 1;
      render();
    });

    elements.resetButton.addEventListener('click', () => {
      state.page = 1;
      state.search = '';
      state.animal = '';
      state.stateFilter = '';
      elements.searchInput.value = '';
      elements.animalFilter.value = '';
      elements.stateFilter.value = '';
      render();
    });

    elements.prevButton.addEventListener('click', () => {
      if (state.page > 1) {
        state.page -= 1;
        render({ scrollToTop: true });
      }
    });

    elements.nextButton.addEventListener('click', () => {
      state.page += 1;
      render({ scrollToTop: true });
    });

    loadStories();
  };

  window.dispatchEvent(new Event('tkc:public-stories-grid-ready'));
})();
